import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PlusIcon, SaveIcon, Trash2Icon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { MoneyInput } from '@/components/ui/money-input'
import { Badge } from '@/components/ui/badge'
import { ProductCombobox } from '@/components/ui/product-combobox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { getLocalizedValue, getResizedPictureUrl } from '@/lib/utils'
import { useMetaStore } from '@/store'
import { useProduct } from '@/hooks'

// ── Types ─────────────────────────────────────────────────────────────────────

function resolveItemPrices(item: OrderItemEntry) {
  const p = item.unitPriceOverride ?? 0
  const rate = item.taxRate ?? 0
  const net = rate > 0 ? p - (p * rate) / (100 + rate) : p
  return { net, gross: p }
}

export type OrderItemEntry = {
  productId?: string
  productCode: string
  productTitle: string
  mainPicturePath?: string
  quantity: number
  unitPriceOverride?: number
  discountRateOverride?: number
  unitNetPrice?: number
  originalGross?: number
  taxRate?: number
  priceIncludesTax?: boolean
  priceManuallyOverridden?: boolean
}

type AddItemDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  currencySymbol?: string
  currencyCode?: string
  onAddItems: (items: OrderItemEntry[]) => void
}

// ── Schema ────────────────────────────────────────────────────────────────────

const buildAddItemSchema = (t: ReturnType<typeof useTranslations>) =>
  z.discriminatedUnion('itemType', [
    z.object({
      itemType: z.literal('registered'),
      productId: z.string().min(1, t('required')),
      quantity: z
        .number({ error: t('required') })
        .int()
        .min(1, t('min_value', { value: 1 })),
      unitPriceOverride: z.number().optional(),
      discountRateOverride: z.number().min(0).max(100).optional(),
      taxRateOverride: z.number().min(0).max(100).optional(),
      priceIncludesTax: z.boolean().optional(),
      priceManuallyOverridden: z.boolean().optional()
    }),
    z.object({
      itemType: z.literal('free'),
      freeProductName: z.string().min(1, t('required')),
      freeProductCode: z.string().min(1, t('required')),
      quantity: z
        .number({ error: t('required') })
        .int()
        .min(1, t('min_value', { value: 1 })),
      unitPriceOverride: z.number().optional(),
      discountRateOverride: z.number().min(0).max(100).optional(),
      taxRate: z.number().min(0).max(100).optional(),
      priceIncludesTax: z.boolean().optional()
    })
  ])

type AddItemFormData = z.infer<ReturnType<typeof buildAddItemSchema>>

// ── AddOrderItemForm ──────────────────────────────────────────────────────────

type AddOrderItemFormProps = {
  currencySymbol?: string
  currencyCode?: string
  defaultItemType?: 'registered' | 'free'
  onAdd: (item: OrderItemEntry) => void
  onCancel: () => void
  onItemTypeChange?: (type: 'registered' | 'free') => void
}

const AddOrderItemForm = ({
  currencySymbol,
  currencyCode,
  defaultItemType = 'registered',
  onAdd,
  onCancel,
  onItemTypeChange
}: AddOrderItemFormProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const defaultTaxRate = metaState.response?.generalSettings.defaultTaxRate ?? 0

  const form = useForm<AddItemFormData>({
    resolver: zodResolver(buildAddItemSchema(t)),
    defaultValues: {
      itemType: defaultItemType,
      productId: '',
      freeProductName: '',
      freeProductCode: '',
      quantity: 1,
      unitPriceOverride: undefined,
      taxRate: defaultTaxRate || undefined,
      priceIncludesTax: false
    } as AddItemFormData
  })

  const itemType = form.watch('itemType')

  const prevPit = useRef<boolean | undefined>(undefined)
  const skipPitConversion = useRef(false)
  const productId = itemType === 'registered' ? (form.watch as (n: string) => string)('productId') : ''
  const { data: product, isLoading: isLoadingProduct } = useProduct(productId, currencyCode)

  useEffect(() => {
    if (itemType === 'registered' && product?.price !== null) {
      const pit = product?.priceIncludesTax ?? false
      const prefill = pit
        ? (product?.price.originalGross ?? product?.price.gross)
        : (product?.price.originalNet ?? product?.price.net)
      form.setValue('unitPriceOverride', prefill)
      form.setValue('discountRateOverride', product?.discountRate ?? undefined)
      form.setValue('taxRateOverride', product?.taxRate ?? undefined)
      form.setValue('priceIncludesTax', pit)
      form.setValue('priceManuallyOverridden', false)
      // ürün yüklemesi sonrası prevPit güncelle — dönüşüm tetiklenmesin
      prevPit.current = pit
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, itemType])

  const allValues = form.watch()
  const watchedPrice = (allValues as Record<string, unknown>)['unitPriceOverride'] as number | undefined
  const watchedDiscountRate = (allValues as Record<string, unknown>)['discountRateOverride'] as number | undefined
  const watchedTaxRateRegistered = (allValues as Record<string, unknown>)['taxRateOverride'] as number | undefined
  const watchedTaxRateFree = (allValues as Record<string, unknown>)['taxRate'] as number | undefined
  const watchedPriceIncludesTax = (allValues as Record<string, unknown>)['priceIncludesTax'] as boolean | undefined
  const watchedQty = (allValues as Record<string, unknown>)['quantity'] as number | undefined
  const watchedTaxRate = itemType === 'registered' ? (watchedTaxRateRegistered ?? 0) : (watchedTaxRateFree ?? 0)

  useEffect(() => {
    const current = watchedPriceIncludesTax
    const prev = prevPit.current
    if (skipPitConversion.current) {
      skipPitConversion.current = false
      prevPit.current = current
      return
    }
    if (prev === undefined || prev === current) {
      prevPit.current = current
      return
    }
    prevPit.current = current
    const price = (form.getValues as (n: string) => number | undefined)('unitPriceOverride') ?? 0
    if (price <= 0) {
      return
    }
    const taxRate =
      itemType === 'registered'
        ? ((form.getValues as (n: string) => number | undefined)('taxRateOverride') ?? 0)
        : ((form.getValues as (n: string) => number | undefined)('taxRate') ?? 0)
    if (taxRate <= 0) {
      return
    }
    if (current === false) {
      const net = price / (1 + taxRate / 100)
      ;(form.setValue as (n: string, v: number) => void)('unitPriceOverride', Math.round(net * 10000) / 10000)
    } else {
      const gross = price * (1 + taxRate / 100)
      ;(form.setValue as (n: string, v: number) => void)('unitPriceOverride', Math.round(gross * 10000) / 10000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPriceIncludesTax])

  const pit = watchedPriceIncludesTax === true

  const previewDiscountedPrice = (() => {
    const base = watchedPrice ?? 0
    return watchedDiscountRate && watchedDiscountRate > 0 ? base * (1 - watchedDiscountRate / 100) : base
  })()

  // previewFinalGross:
  const previewDiscountedNet = previewDiscountedPrice
  const previewFinalGross = pit
    ? previewDiscountedPrice
    : watchedTaxRate > 0
      ? previewDiscountedPrice * (1 + watchedTaxRate / 100)
      : previewDiscountedPrice

  const handleSubmit = (data: AddItemFormData) => {
    if (data.itemType === 'registered') {
      if (!product) {
        return
      }
      const pit = data.priceIncludesTax === true
      const basePrice = data.unitPriceOverride ?? 0
      const discountRate = data.discountRateOverride ?? 0
      const taxRate = data.taxRateOverride ?? 0
      const discountedPrice = discountRate > 0 ? basePrice * (1 - discountRate / 100) : basePrice
      const productTitle = getLocalizedValue(product, 'title', locale, defaultLocale) || product.code
      onAdd({
        productId: product.id,
        productCode: product.code,
        productTitle,
        mainPicturePath: product.mainPicture?.path,
        quantity: data.quantity,
        unitPriceOverride: discountedPrice,
        discountRateOverride: discountRate > 0 ? discountRate : undefined,
        taxRate: taxRate > 0 ? taxRate : undefined,
        originalGross: discountRate > 0 ? basePrice : undefined,
        priceIncludesTax: pit,
        priceManuallyOverridden: data.priceManuallyOverridden === true
      })
    } else {
      const pit = data.priceIncludesTax !== false
      const basePrice = data.unitPriceOverride ?? 0
      const discountRate = data.discountRateOverride ?? 0
      const discountedPrice = discountRate > 0 ? basePrice * (1 - discountRate / 100) : basePrice
      onAdd({
        productId: undefined,
        productCode: data.freeProductCode || 'FREE',
        productTitle: data.freeProductName,
        mainPicturePath: undefined,
        quantity: data.quantity,
        unitPriceOverride: discountedPrice,
        discountRateOverride: discountRate > 0 ? discountRate : undefined,
        originalGross: discountRate > 0 ? basePrice : undefined,
        taxRate: data.taxRate,
        priceIncludesTax: pit
      })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
        {/* Item type radio */}
        <FormField
          control={form.control}
          name="itemType"
          render={({ field }) => (
            <FormItem className="flex flex-col justify-start">
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={v => {
                    field.onChange(v)
                    onItemTypeChange?.(v as 'registered' | 'free')
                  }}
                  className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="registered" id="item-type-registered" />
                    <Label htmlFor="item-type-registered">{t('registered_product')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="free" id="item-type-free" />
                    <Label htmlFor="item-type-free">{t('free_product')}</Label>
                  </div>
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />

        {itemType === 'registered' ? (
          <FormField
            control={form.control}
            name="productId"
            render={({ field, fieldState }) => (
              <FormItem className="flex flex-col justify-start">
                <FormLabel required>{t('product')}</FormLabel>
                <FormControl>
                  <ProductCombobox
                    value={(field.value as string) ?? ''}
                    onValueChange={v => field.onChange(v as string)}
                    clearable
                    error={!!fieldState.error}
                    currencyCode={currencyCode}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="freeProductName"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col justify-start">
                  <FormLabel required>{t('product_name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={(field.value as string) ?? ''}
                      placeholder={t('product_name')}
                      className={fieldState.error ? 'border-destructive' : ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="freeProductCode"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col justify-start">
                  <FormLabel required>{t('product_code')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={(field.value as string) ?? ''}
                      placeholder="SKU"
                      className={fieldState.error ? 'border-destructive' : ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {itemType === 'registered' && isLoadingProduct && !!productId && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        )}

        {(itemType !== 'registered' || (!!product && !isLoadingProduct)) && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem className="flex flex-col justify-start">
                    <FormLabel required>{t('quantity')}</FormLabel>
                    <FormControl>
                      <NumberInput min={1} stepper={1} value={field.value} onValueChange={v => field.onChange(v)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unitPriceOverride"
                render={({ field }) => (
                  <FormItem className="flex flex-col justify-start">
                    <FormLabel>
                      {t('unit_price')}{' '}
                      {currencySymbol && <span className="text-muted-foreground font-normal">({currencySymbol})</span>}
                    </FormLabel>
                    <FormControl>
                      <MoneyInput
                        currencySymbol={currencySymbol}
                        value={field.value}
                        onValueChange={v => {
                          field.onChange(v)
                          if (itemType === 'registered') {
                            form.setValue('priceManuallyOverridden', true)
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="discountRateOverride"
                render={({ field }) => (
                  <FormItem className="flex flex-col justify-start">
                    <FormLabel>{t('discount_rate')}</FormLabel>
                    <FormControl>
                      <NumberInput
                        min={0}
                        max={100}
                        stepper={0.5}
                        decimalScale={2}
                        suffix="%"
                        value={field.value}
                        onValueChange={v => field.onChange(v)}
                        placeholder="0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {itemType === 'registered' ? (
                <FormField
                  control={form.control}
                  name="taxRateOverride"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-start">
                      <FormLabel>{t('tax_rate')}</FormLabel>
                      <FormControl>
                        <NumberInput
                          min={0}
                          max={100}
                          stepper={0.5}
                          decimalScale={2}
                          suffix="%"
                          value={field.value}
                          onValueChange={v => field.onChange(v)}
                          placeholder="0.00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="taxRate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-start">
                      <FormLabel>{t('tax_rate')}</FormLabel>
                      <FormControl>
                        <NumberInput
                          min={0}
                          max={100}
                          stepper={0.5}
                          decimalScale={2}
                          suffix="%"
                          value={field.value}
                          onValueChange={v => field.onChange(v)}
                          placeholder="0.00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="priceIncludesTax"
                render={({ field }) => (
                  <FormItem className="flex flex-col justify-start">
                    <FormLabel>{t('price_includes_tax')}</FormLabel>
                    <FormControl className="mt-2">
                      <Switch checked={field.value as boolean} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Price summary */}
            {watchedPrice !== undefined && watchedPrice > 0 && (
              <div className="bg-muted/50 space-y-1 rounded-md px-3 py-2 text-sm">
                {(watchedDiscountRate ?? 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('discounted_price')}:</span>
                    <span className="font-medium text-green-700 dark:text-green-400">
                      {previewDiscountedNet.toFixed(2)} {currencySymbol ?? ''}
                      <span className="text-muted-foreground ml-1 text-xs">
                        (-{((watchedPrice ?? 0) - previewDiscountedNet).toFixed(2)} {currencySymbol ?? ''})
                      </span>
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('gross_price')}
                    {pit ? ` (${t('incl_tax')})` : watchedTaxRate > 0 ? ` (+ %${watchedTaxRate})` : ''}:
                  </span>
                  <span className="font-semibold">
                    {previewFinalGross.toFixed(2)} {currencySymbol ?? ''}
                  </span>
                </div>
                {(watchedQty ?? 1) >= 1 && (
                  <div className="border-border/50 mt-1 flex justify-between border-t pt-1">
                    <span className="text-muted-foreground">
                      {watchedQty ?? 1} × {previewFinalGross.toFixed(2)} {currencySymbol ?? ''}
                    </span>
                    <span className="text-primary font-semibold">
                      = {((watchedQty ?? 1) * previewFinalGross).toFixed(2)} {currencySymbol ?? ''}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            <XIcon />
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={itemType === 'registered' && isLoadingProduct}>
            <PlusIcon />
            {t('add_to_list')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}

// ── AddOrderItemDialog ────────────────────────────────────────────────────────

export const AddOrderItemDialog = ({
  open,
  onOpenChange,
  currencySymbol,
  currencyCode,
  onAddItems
}: AddItemDialogProps) => {
  const t = useTranslations()
  const { metaState } = useMetaStore()
  const defaultCurrencyCode = metaState.response?.generalSettings.defaultCurrencyCode
  const resolvedCurrencySymbol =
    currencySymbol ??
    (metaState.response?.currencies.find(c => c.code === defaultCurrencyCode)?.symbol || defaultCurrencyCode || '')

  const [formKey, setFormKey] = useState(0)
  const [pendingItems, setPendingItems] = useState<OrderItemEntry[]>([])
  const [defaultItemType, setDefaultItemType] = useState<'registered' | 'free'>('registered')

  const resetDialog = () => {
    setPendingItems([])
    setDefaultItemType('registered')
    setFormKey(k => k + 1)
  }

  const handleClose = () => {
    resetDialog()
    onOpenChange(false)
  }

  const handleAddToPending = (item: OrderItemEntry) => {
    setPendingItems(prev => {
      const existingIdx = item.productId
        ? prev.findIndex(p => p.productId === item.productId)
        : prev.findIndex(p => !p.productId && p.productCode === item.productCode)
      if (existingIdx >= 0) {
        return prev.map((p, i) =>
          i === existingIdx
            ? {
                ...p,
                quantity: p.quantity + item.quantity,
                unitPriceOverride: item.unitPriceOverride ?? p.unitPriceOverride
              }
            : p
        )
      }
      return [...prev, item]
    })
    setDefaultItemType(item.productId ? 'registered' : 'free')
    setFormKey(k => k + 1)
  }

  const handleSubmit = () => {
    if (pendingItems.length === 0) {
      return
    }
    onAddItems(pendingItems)
    resetDialog()
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) {
          handleClose()
        }
      }}>
      <DialogContent className="sm:max-w-2xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('add_item')}</DialogTitle>
        </DialogHeader>
        <AddOrderItemForm
          key={formKey}
          currencySymbol={resolvedCurrencySymbol}
          currencyCode={currencyCode}
          defaultItemType={defaultItemType}
          onAdd={handleAddToPending}
          onCancel={handleClose}
          onItemTypeChange={type => {
            setDefaultItemType(type)
            setFormKey(k => k + 1)
          }}
        />
        {pendingItems.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm font-medium">
                {t('pending_items')} ({pendingItems.length})
              </p>
              <ScrollArea className="h-48">
                <div className="space-y-1 pr-2">
                  {pendingItems.map((item, idx) => {
                    const { net, gross } = resolveItemPrices(item)
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          {item.mainPicturePath && (
                            <Image
                              src={getResizedPictureUrl(item.mainPicturePath, 32, 32)}
                              alt={item.productTitle}
                              width={32}
                              height={32}
                              className="h-8 w-8 shrink-0 rounded object-cover"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground font-mono text-xs">{item.productCode}</span>
                              <span className="truncate font-medium">{item.productTitle}</span>
                            </div>
                            {item.taxRate !== null && (
                              <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                                <Badge variant="outline" className="px-1 py-0 text-xs">
                                  %{item.taxRate}
                                </Badge>
                                <span>
                                  {t('price_without_tax')}:{' '}
                                  <span className="font-medium">
                                    {net.toFixed(2)} {resolvedCurrencySymbol}
                                  </span>
                                </span>
                                <span>·</span>
                                <span>
                                  {t('price_with_tax')}:{' '}
                                  <span className="font-medium">
                                    {gross.toFixed(2)} {resolvedCurrencySymbol}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-2 flex shrink-0 items-center gap-2">
                          <span className="text-muted-foreground text-xs">x{item.quantity}</span>
                          <span className="font-medium">
                            {(gross * item.quantity).toFixed(2)} {resolvedCurrencySymbol}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-destructive h-6 w-6"
                            onClick={() => setPendingItems(prev => prev.filter((_, i) => i !== idx))}>
                            <Trash2Icon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                <XIcon />
                {t('cancel')}
              </Button>
              <Button type="button" onClick={handleSubmit}>
                <SaveIcon />
                {t('add_to_order')} ({pendingItems.length})
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
